import {
  Fragment,
  useEffect,
  useState,
} from 'react';
import {
  Callout,
  Field,
  FilterBar,
  Facts,
  Select,
  Chip,
  Empty,
  Loading,
  Panel,
  TableScroll,
  formatCount,
  useLoad,
} from '@affdice/portal-kit';
import {
  PlatformApi,
  type CursorBrand,
} from '../../client.ts';
import {
  consumerLine,
  consumersNeedingAttentionFirst,
} from '../../screens.ts';
import { t, tCount } from '../../messages.ts';
import { Failure } from '../shared.tsx';

export function Consumers({ api }: { api: PlatformApi }) {
  return (
    <>
      <div className="page-head"><h1>{t('nav.consumers')}</h1></div>
      <EventCursors api={api} />
    </>
  );
}

/**
 * Where each consumer has got to in the ledger, and how many events that is —
 * for one brand, or for every brand. The brand list comes back with the
 * cursors and is kept across loads, so the filter stays on screen while a
 * newly chosen brand's counts load.
 */
function EventCursors({ api }: { api: PlatformApi }) {
  const [brandId, setBrandId] = useState('');
  const [brands, setBrands] = useState<readonly CursorBrand[]>([]);
  const loaded = useLoad(() => api.consumers(brandId ? { brandId } : {}), [brandId]);
  useEffect(() => {
    if (loaded.state === 'ready') setBrands(loaded.value.brands);
  }, [loaded]);

  const ready = loaded.state === 'ready' ? loaded.value : null;
  const lines = consumersNeedingAttentionFirst(
    ready ? ready.items.map((c) => consumerLine(c, new Date(ready.checked_at))) : [],
  );
  const wrong = lines.filter((l) => l.state !== 'MOVING').length;
  // Batch 2 item 6 — a stalled consumer is worth a look, not yet a failure;
  // the aside only escalates to overdue when something is actually failing,
  // which the dashboard already flags in the console's own accent for the
  // same reason (§12.1 — attention took the console accent, not a fixed
  // amber).
  const anyFailing = lines.some((l) => l.state === 'FAILING');
  const chosen = brands.find((b) => b.id === brandId);

  return (
    <>
      <Panel
        title={t('integrity.consumers.cursors')}
        aside={
          !ready ? null : wrong > 0 ? (
            <Chip tone={anyFailing ? 'overdue' : 'attention'}>{tCount('integrity.consumers.notMoving', wrong)}</Chip>
          ) : (
            <Chip tone="locked">{t('integrity.consumers.allMoving')}</Chip>
          )
        }
      >
        <div className="stack">
          <FilterBar>
            <Field label={t('common.brand')}>
              <Select
                value={brandId}
                onChange={setBrandId}
                options={[
                  { value: '', label: t('integrity.consumers.allBrands') },
                  ...brands.map((b) => ({ value: b.id, label: t('integrity.consumers.brandOption', { brand: b.name, operator: b.operator }) })),
                ]}
              />
            </Field>
          </FilterBar>

          {loaded.state === 'loading' ? <Loading rows={2} /> : null}
          {loaded.state === 'error' ? <Failure error={loaded.error} /> : null}

          {ready ? (
            <>
              <Facts
                items={[
                  { label: chosen ? t('integrity.consumers.callsFor', { brand: chosen.name }) : t('integrity.consumers.callsAll'), value: formatCount(ready.total) },
                ]}
              />
              {lines.length === 0 ? (
                <Empty>{t('integrity.consumers.empty')}</Empty>
              ) : (
                <TableScroll>
                  <table>
                    <thead>
                      <tr>
                        <th>{t('integrity.consumers.consumer')}</th>
                        <th className="num">{t('integrity.consumers.calls')}</th>
                        <th className="num">{t('integrity.consumers.waiting')}</th>
                        <th className="num">{t('integrity.consumers.lastMoved')}</th>
                        <th className="num">{t('integrity.consumers.failed')}</th>
                        <th>{t('common.state')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Batch 2 item 13 — the note and its error (when both
                          are present) stretched this row over several lines;
                          they move to a row note below, spanning the six
                          columns above (Consumer, Calls, Waiting, Last moved,
                          Failed, State). */}
                      {lines.map((l) => {
                        const noteLines = [l.note, l.lastError].filter(
                          (line): line is string => Boolean(line),
                        );
                        return (
                          <Fragment key={l.name}>
                            <tr className={noteLines.length > 0 ? 'has-note' : ''}>
                              <td className="mono">{l.name}</td>
                              <td className="num">{l.calls}</td>
                              <td className="num">{l.waiting}</td>
                              <td className="num faint">{l.lastMoved}</td>
                              <td className="num">{l.failed || '—'}</td>
                              <td>
                                {l.state === 'MOVING' ? <Chip tone="locked">{t('integrity.consumers.moving')}</Chip> : null}
                                {/* Batch 2 item 6 — stalled is worth a look,
                                    not yet a failure; failing keeps overdue. */}
                                {l.state === 'STALLED' ? <Chip tone="attention">{t('integrity.consumers.stalled')}</Chip> : null}
                                {l.state === 'NOT_STARTED' ? <Chip tone="attention">{t('integrity.consumers.notStarted')}</Chip> : null}
                                {l.state === 'FAILING' ? <Chip tone="overdue">{t('integrity.consumers.failing')}</Chip> : null}
                              </td>
                            </tr>
                            {noteLines.length > 0 ? (
                              <tr className="row-note">
                                <td colSpan={6}>
                                  {noteLines.map((line, i) => (
                                    <span key={i} className="row-note-text">{line}</span>
                                  ))}
                                </td>
                              </tr>
                            ) : null}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </TableScroll>
              )}
            </>
          ) : null}
        </div>
      </Panel>

      {/* QA round 2 item I1 — the server's note explains Calls and Waiting
          for an event reader; a scheduled job has neither, and shows a dash. */}
      {ready ? (
        <Callout>
          {ready.note} {t('integrity.consumers.dashNote')}
        </Callout>
      ) : null}
    </>
  );
}
